import { ROTransaction, RWTransaction, runWithROTransaction } from '../../../db'
import {
  BaseDBService,
  BaseDBServiceOpts,
  Context,
  CoreDict,
  Id,
  createObj,
  enumGetValues,
  localTimeParse,
  runIfDefined,
  throwObjectNotFound,
  validateOptionalNumber,
} from '../../../common'
import {
  GLOBAL_SETTINGS_ID,
  GlobalSettings,
  GlobalSettingsRepository,
  GlobalSettingsService,
  UpdateGlobalSettingsOpts,
} from './globalSettings'
import { Permissions } from '../../authorization'
import { CreateEventMode, ModelEvent, ModelEventCreator, ModelEventsProcessor } from '../../events'
import { RolesRepository } from '../../users'
import { LocalTime } from '@js-joda/core'

enum EventType {
  SETTINGS_UPDATE = 'SETTINGS_UPDATE',
}

type UpdateGlobalSettingsPayload = {
  anonymousRoleId?: Id
  reportsCloseTime?: string
  cardActivationDelay?: number
}

interface DBGlobalSettingsServiceOpts extends BaseDBServiceOpts<CoreDict> {
  globalSettingsRepository: GlobalSettingsRepository
  rolesRepository: RolesRepository
}

export class DBGlobalSettingsService
  extends BaseDBService<DBGlobalSettingsServiceOpts, CoreDict>
  implements GlobalSettingsService
{
  private validateCardActivationDelay = (cardActivationDelay?: number) =>
    validateOptionalNumber('<3c26be82> Card Activation Delay', cardActivationDelay, { minValue: 1, maxValue: 31 })

  constructor(opts: DBGlobalSettingsServiceOpts) {
    super('DBGlobalSettingsService', opts)
    this.logger.debug({}, '<5364ae2c> Initialized')
  }

  async load(ctx: Context): Promise<GlobalSettings | undefined> {
    await this.opts.permissionsService.checkPermission(ctx, Permissions.GLOBAL_SETTINGS_VIEW)
    return await runWithROTransaction(this.opts.dbConnection, tx =>
      this.opts.globalSettingsRepository.load(tx, GLOBAL_SETTINGS_ID),
    )
  }

  async update(ctx: Context, opts: UpdateGlobalSettingsOpts): Promise<void> {
    await this.opts.permissionsService.checkPermission(ctx, Permissions.GLOBAL_SETTINGS_MANAGE)
    const event = await runWithROTransaction(this.opts.dbConnection, async tx => {
      if (opts.anonymousRoleId) {
        ;(await this.opts.rolesRepository.findByIdNonDeleted(tx, opts.anonymousRoleId)) ??
          throwObjectNotFound(`<50484ad2> Role not found by id ${opts.anonymousRoleId}`)
      }

      return await this.opts.eventCreator.create(ctx, tx, {
        mode: CreateEventMode.NEW_OR_UPDATE,
        entityId: GLOBAL_SETTINGS_ID,
        type: EventType.SETTINGS_UPDATE,
        payload: <UpdateGlobalSettingsPayload>{
          anonymousRoleId: opts.anonymousRoleId,
          reportsCloseTime: runIfDefined(opts.reportsCloseTime, s => localTimeParse(s).toString()),
          cardActivationDelay: this.validateCardActivationDelay(opts.cardActivationDelay),
        },
      })
    })

    await this.opts.eventDispatcher.processEvents(ctx, [event])
  }
}

export class GlobalSettingsModelEventProcessor implements ModelEventsProcessor {
  supportedEventTypes: string[] = enumGetValues(EventType)

  constructor(private opts: { globalSettingsRepository: GlobalSettingsRepository; eventCreator: ModelEventCreator }) {}

  // UPDATED
  async createMergeEvent(ctx: Context, tx: ROTransaction, entityId: string): Promise<ModelEvent> {
    const s =
      (await this.opts.globalSettingsRepository.load(tx, GLOBAL_SETTINGS_ID)) ?? throwObjectNotFound('<66c243bf>')
    return await this.opts.eventCreator.create(ctx, tx, {
      entityId: entityId,
      mode: CreateEventMode.UPDATE,
      type: EventType.SETTINGS_UPDATE,
      payload: createObj<UpdateGlobalSettingsPayload>({
        ...s,
        reportsCloseTime: s.reportsCloseTime?.toString(),
      }),
    })
  }

  async process(ctx: Context, tx: RWTransaction, event: ModelEvent): Promise<boolean> {
    switch (event.type) {
      case EventType.SETTINGS_UPDATE: {
        const p = event.payload as UpdateGlobalSettingsPayload
        const settings: GlobalSettings = (await this.opts.globalSettingsRepository.load(tx, GLOBAL_SETTINGS_ID)) ?? {
          id: GLOBAL_SETTINGS_ID,
          createdAt: event.createdAt,
        }

        await this.opts.globalSettingsRepository.save(tx, {
          ...settings,
          anonymousRoleId: p.anonymousRoleId ?? settings.anonymousRoleId,
          reportsCloseTime: runIfDefined(p.reportsCloseTime, s => LocalTime.parse(s)) ?? settings.reportsCloseTime,
          cardActivationDelay: p.cardActivationDelay ?? settings.cardActivationDelay,
        })

        return true
      }
    }
    return false
  }
}
